import type { SVGProps } from 'react';
import spriteFile from '../../assets/zarya-sprite.svg?url';

/**
 * Nomes dos símbolos do sprite `src/assets/zarya-sprite.svg` (cópia de
 * `docs/design/zarya-sprite.svg`) — sem o prefixo `i-`.
 * Manter em sincronia com o sprite; ícone novo entra lá primeiro.
 */
export const ICON_NAMES = [
  'apps',
  'arrow-down',
  'arrow-right',
  'ban',
  'bell',
  'bolt',
  'broadcast',
  'check',
  'check-circle',
  'chevron',
  'chevron-down',
  'chevron-left',
  'clock',
  'close',
  'cloud',
  'code',
  'compress',
  'csv',
  'database',
  'download',
  'exchange',
  'expand',
  'eye',
  'file-search',
  'file-shield',
  'filter',
  'flag',
  'gauge',
  'gear',
  'home',
  'info',
  'key',
  'link',
  'list',
  'lock',
  'menu',
  'moon',
  'palette',
  'pause',
  'pen-square',
  'person',
  'play',
  'plus',
  'power',
  'question',
  'refresh',
  'robot',
  'search',
  'server',
  'shield',
  'shield-alert',
  'sun',
  'switch',
  'table',
  'trash',
  'user',
  'users',
  'warning',
] as const;

export type IconName = (typeof ICON_NAMES)[number];
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASS: Record<IconSize, string> = {
  xs: 'ic-xs',
  sm: 'ic-sm',
  md: '',
  lg: 'ic-lg',
  xl: 'ic-xl',
};

/**
 * Caminho servido do sprite, COM impressão digital de conteúdo. O `<use href>`
 * aponta para ele + `#i-<nome>`.
 *
 * # POR QUE O SPRITE SAIU DE `public/` (15/09)
 *
 * Em `public/` ele era servido como `/zarya-sprite.svg` — nome fixo, e o nginx não
 * manda `Cache-Control`. O pacote JavaScript tem digest no nome e chega sempre novo;
 * o sprite não tinha, e ficava no cache heurístico do navegador. Quando o marco de
 * 14/09 acrescentou `i-pen-square` e `i-file-search`, quem já tinha visitado a
 * aplicação recebeu pacote novo com sprite velho: o `<use>` não resolvia, e os
 * botões de «editar» e «ver detalhes» desenhavam NADA, com a caixa de 36 px ainda
 * clicável. Foi o defeito que o cliente reportou em 15/09.
 *
 * Importado como recurso, o Vite emite `assets/zarya-sprite-<hash>.svg`: sprite novo
 * é URL nova, e nenhum cache antigo a pode responder. Mesmo contrato do pacote.
 */
export const SPRITE_URL: string = spriteFile;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: IconSize;
  /** Rótulo acessível. Sem ele o ícone é decorativo (`aria-hidden`). */
  title?: string;
}

/**
 * Ícone do sprite Zarya. Renderiza `<svg class="ic"><use href="<sprite>#i-nome"/></svg>`,
 * exatamente como a função `ico()` documentada no cabeçalho do sprite.
 */
export function Icon({ name, size = 'md', title, className, ...rest }: IconProps) {
  const classes = ['ic', SIZE_CLASS[size], className].filter(Boolean).join(' ');
  const decorative = !title;

  return (
    <svg
      className={classes}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative || undefined}
      aria-label={title}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <use href={`${SPRITE_URL}#i-${name}`} />
    </svg>
  );
}
